// src/post.ts
// The PenPal Post: the site's little mail service and journal.
//
// Data (checked by firestore.rules):
// - postPieces/{id}: kind ('submission' or 'dispatch'), category, title,
//   body (rich-text HTML), color, cw?, anonymous, uid?, username,
//   status ('sorting' -> 'delivered' or 'returned'), editorNote?,
//   submittedAt, deliveredAt?, removed
// - postPieces/{id}/replies/{rid}: comments, like room comments
// - editors/{uid}: who can deliver pieces and post dispatches (added by hand
//   in the Firebase console)
// - roomAuthors/pp_{id}[_{rid}]: hidden author of an anonymous piece/comment
// - users/{uid}/private/rooms lists your pieces and comments (postPieces,
//   postReplies) so you can see their status and delete/download them.
import { useEffect, useState } from "react";
import {
  Timestamp,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import { checkPace, markPace } from "./pace";
import { LIMITS } from "./rooms";
import { emailNewSubmission } from "./reportEmail";

export const POST_CATEGORIES = [
  "Poetry",
  "Essays",
  "Short Stories",
  "Letter Excerpts",
  "Dear Future Me",
  "Community History",
  "Pen Pal Stories",
  "Art & Zine Notes",
  "Wisdom from Elders",
  "Love Letters to Ourselves",
];

export type Piece = {
  id: string;
  kind: "submission" | "dispatch";
  category?: string;
  title: string;
  body: string;
  color?: string;
  cw?: string;
  anonymous?: boolean;
  uid?: string;
  username: string;
  status: "sorting" | "delivered" | "returned";
  editorNote?: string;
  submittedAt?: Timestamp;
  deliveredAt?: Timestamp;
  removed?: boolean;
};

export const piecesCol = collection(db, "postPieces");
export const pieceRef = (id: string) => doc(db, "postPieces", id);
export const pieceRepliesCol = (id: string) => collection(db, "postPieces", id, "replies");
const authorRef = (key: string) => doc(db, "roomAuthors", key);
const mineRef = (uid: string) => doc(db, "users", uid, "private", "rooms");

// "Autumn 2026": the seasonal "delivery" a piece arrived in.
export function deliveryName(t?: { toDate: () => Date }) {
  if (!t) return "";
  const d = t.toDate();
  const m = d.getMonth();
  const season = m < 2 || m === 11 ? "Winter" : m < 5 ? "Spring" : m < 8 ? "Summer" : "Autumn";
  return `${season} ${m === 11 ? d.getFullYear() + 1 : d.getFullYear()}`;
}

// A postmark-style date, e.g. "OCT 29 2026".
export function postmark(t?: { toDate: () => Date }) {
  if (!t) return "";
  return t
    .toDate()
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    .replace(",", "")
    .toUpperCase();
}

export async function submitPiece(
  uid: string,
  username: string,
  p: { category: string; subject: string; message: string; color: string; cw: string; anonymous: boolean }
) {
  const ref = doc(piecesCol);
  await checkPace(uid, "post");
  const batch = writeBatch(db);
  markPace(batch, uid, "post");
  if (p.anonymous) batch.set(authorRef(`pp_${ref.id}`), { uid });
  batch.set(ref, {
    kind: "submission",
    category: p.category,
    title: p.subject.trim().slice(0, LIMITS.title),
    body: p.message.slice(0, 30000),
    color: p.color,
    ...(p.cw ? { cw: p.cw.slice(0, LIMITS.cw) } : {}),
    ...(p.anonymous ? { anonymous: true, username: "anonymous" } : { anonymous: false, username, uid }),
    status: "sorting",
    submittedAt: serverTimestamp(),
    removed: false,
  });
  await batch.commit();
  await setDoc(mineRef(uid), { postPieces: arrayUnion(ref.id) }, { merge: true });
  emailNewSubmission({ id: ref.id, title: p.subject, category: p.category, username: p.anonymous ? "anonymous" : username }).catch(
    (err) => console.error("Submission email failed", err)
  );
  return ref.id;
}

// ---------- editors ----------

export function useIsEditor(uid?: string) {
  const [editor, setEditor] = useState(false);
  useEffect(() => {
    if (!uid) return setEditor(false);
    getDoc(doc(db, "editors", uid))
      .then((s) => setEditor(s.exists()))
      .catch(() => setEditor(false));
  }, [uid]);
  return editor;
}

export const deliverPiece = (id: string) =>
  updateDoc(pieceRef(id), { status: "delivered", deliveredAt: serverTimestamp(), editorNote: "" });
export const returnPiece = (id: string, note: string) =>
  updateDoc(pieceRef(id), { status: "returned", editorNote: note.trim().slice(0, 1000) });
export const deletePiece = (id: string) => deleteDoc(pieceRef(id));

export async function postDispatch(
  uid: string,
  username: string,
  p: { subject: string; message: string; color: string; cw: string }
) {
  const ref = doc(piecesCol);
  await checkPace(uid, "post");
  const batch = writeBatch(db);
  markPace(batch, uid, "post");
  batch.set(ref, {
    kind: "dispatch",
    title: p.subject.trim().slice(0, LIMITS.title),
    body: p.message.slice(0, 30000),
    color: p.color,
    ...(p.cw ? { cw: p.cw.slice(0, LIMITS.cw) } : {}),
    anonymous: false,
    uid,
    username,
    status: "delivered",
    submittedAt: serverTimestamp(),
    deliveredAt: serverTimestamp(),
    removed: false,
  });
  await batch.commit();
  return ref.id;
}

// ---------- comments and taking things back ----------

export async function commentOnPiece(
  uid: string,
  username: string,
  id: string,
  body: string,
  cw: string,
  anonymous: boolean
) {
  const ref = doc(pieceRepliesCol(id));
  await checkPace(uid, "comment");
  const batch = writeBatch(db);
  markPace(batch, uid, "comment");
  if (anonymous) batch.set(authorRef(`pp_${id}_${ref.id}`), { uid });
  batch.set(ref, {
    body: body.trim().slice(0, LIMITS.reply),
    ...(cw.trim() ? { cw: cw.trim().slice(0, LIMITS.cw) } : {}),
    ...(anonymous ? { anonymous: true, username: "anonymous" } : { anonymous: false, username, uid }),
    removed: false,
    createdAt: serverTimestamp(),
  });
  await batch.commit();
  await setDoc(mineRef(uid), { postReplies: arrayUnion(`${id}_${ref.id}`) }, { merge: true });
}

export const takeBackPiece = (id: string) =>
  updateDoc(pieceRef(id), { removed: true, title: "(removed)", body: "", cw: "" });
export const takeBackPieceComment = (id: string, rid: string) =>
  updateDoc(doc(pieceRepliesCol(id), rid), { removed: true, body: "", cw: "" });

// Your own submissions, with their status (anonymous ones included).
export async function getMyPieces(uid: string): Promise<Piece[]> {
  const mine = await getDoc(mineRef(uid)).catch(() => null);
  const ids = ((mine?.data()?.postPieces as string[]) || []).slice(-50);
  const pieces = await Promise.all(
    ids.map((id) =>
      getDoc(pieceRef(id))
        .then((s) => (s.exists() ? ({ id: s.id, ...s.data() } as Piece) : null))
        .catch(() => null)
    )
  );
  return pieces
    .filter((p): p is Piece => !!p && !p.removed)
    .sort((a, b) => (b.submittedAt?.toMillis() ?? 0) - (a.submittedAt?.toMillis() ?? 0));
}
